import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuZap,
    LuLayers,
    LuBrain,
    LuUsers,
    LuBriefcase,
    LuSearch,
    LuPenTool,
    LuCode,
    LuSparkles,
    LuActivity,
    LuCheck,
    LuRefreshCw,
    LuArrowRight,
    LuCalendar
} from 'react-icons/lu';

const AIWorkflowAutomation = () => {
    const [hoveredStack, setHoveredStack] = useState<number | null>(null);
    const [hoveredUseCase, setHoveredUseCase] = useState<string | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            {/* Hero Section */}
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-primary py-20 lg:py-32">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center space-x-2 text-blue-100 font-bold tracking-wider text-sm mb-6 uppercase">
                        <span>Intelligent Operations</span>
                    </div>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
                        AI-Powered <span className="text-secondary">Workflow Automation</span>
                    </h1>
                    <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Eliminate repetitive tasks and bring decision-making intelligence into your business processes. We build smart workflows that understand, decide, and act.
                    </p>
                </div>
            </section>

            {/* Beyond Traditional Automation Section */}
            <section className="py-24 bg-white">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                                <span>What It Is</span>
                            </div>
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Beyond Traditional Automation</h2>
                            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                                Traditional tools handle repetitive actions. AI-powered workflows bring decision-making, personalization, and adaptability into the mix.
                            </p>

                            <div className="space-y-4">
                                {[
                                    "Understand unstructured data (emails, PDFs, chats)",
                                    "Make intelligent decisions (lead routing, escalation)",
                                    "Trigger personalized actions sitewide",
                                    "Multi-tool orchestration (Slack, HubSpot, Notion)"
                                ].map((item, index) => (
                                    <div key={index} className="flex items-start">
                                        <div className="flex-shrink-0 mt-1">
                                            {React.createElement(LuCheck as any, { className: "w-6 h-6 text-slate-900" })}
                                        </div>
                                        <p className="ml-4 text-slate-700 font-medium">{item}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative">
                            <div className="relative rounded-[2.5rem] overflow-hidden aspect-square shadow-2xl flex items-center justify-center p-8 group">
                                <img
                                    src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80"
                                    alt="Workflow Automation Network"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-slate-900/40"></div>

                                <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-12 rounded-3xl text-center shadow-xl w-64 h-64 flex flex-col items-center justify-center relative z-10 transition-transform duration-500 group-hover:scale-105 hover:bg-white/20">
                                    {React.createElement(LuRefreshCw as any, { className: "w-12 h-12 text-white mb-4 animate-spin-slow" })}
                                    <div className="text-white font-bold text-lg">Seamless</div>
                                    <div className="text-blue-100">Connectivity</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tech Stack Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>Our Tech Stack</span>
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900">Platforms We Orchestrate</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: LuZap,
                                title: "n8n.io",
                                desc: "Flexible, open-source automation for custom enterprise logic and data privacy.",
                                color: "text-orange-500",
                                bg: "bg-orange-50",
                                hex: "#f97316"
                            },
                            {
                                icon: LuLayers,
                                title: "Zapier / Make",
                                desc: "Connecting 5,000+ apps with rapid deployment and deep logic capabilities.",
                                color: "text-purple-600",
                                bg: "bg-purple-50",
                                hex: "#9333ea"
                            },
                            {
                                icon: LuBrain,
                                title: "OpenAI / Claude",
                                desc: "Adding LLM intelligence for classification, summarization, and human-like actions.",
                                color: "text-green-600",
                                bg: "bg-green-50",
                                hex: "#16a34a"
                            }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-[2rem] p-10 border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center flex flex-col items-center group"
                                onMouseEnter={() => setHoveredStack(index)}
                                onMouseLeave={() => setHoveredStack(null)}
                                style={{
                                    backgroundImage: hoveredStack === index
                                        ? `linear-gradient(to bottom right, ${item.hex}0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className={`w-20 h-20 ${item.bg} rounded-full flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110`}
                                    style={{ boxShadow: `0 10px 15px -3px ${item.hex}33` }}>
                                    {React.createElement(item.icon as any, { className: `w-8 h-8 ${item.color}` })}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-4 transition-colors duration-300"
                                    style={{ color: hoveredStack === index ? item.hex : undefined }}>
                                    {item.title}
                                </h3>
                                <p className="text-slate-600 leading-relaxed text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Applications Section */}
            <section className="py-24 bg-white">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>Applications</span>
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900">Workflow Use Cases</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div
                            className="bg-white rounded-[2.5rem] p-12 border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center group relative overflow-hidden"
                            onMouseEnter={() => setHoveredUseCase('sales')}
                            onMouseLeave={() => setHoveredUseCase(null)}
                            style={{
                                backgroundImage: hoveredUseCase === 'sales'
                                    ? `linear-gradient(to bottom right, #2563eb0d, transparent)`
                                    : undefined
                            }}
                        >
                            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
                                style={{ boxShadow: `0 10px 15px -3px #2563eb4d` }}>
                                {React.createElement(LuUsers as any, { className: "w-8 h-8" })}
                            </div>
                            <h3 className="text-3xl font-bold text-slate-900 mb-8 transition-colors duration-300"
                                style={{ color: hoveredUseCase === 'sales' ? '#2563eb' : undefined }}>Sales & Marketing</h3>
                            <ul className="space-y-4 text-left inline-block relative z-10">
                                {[
                                    "Auto-follow up with leads from CRM",
                                    "AI-generated personalized proposals",
                                    "Outreach email automation with smart context",
                                    "Lead qualification and multi-channel tagging"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center text-slate-600 font-medium">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 flex-shrink-0"
                                            style={{ backgroundColor: hoveredUseCase === 'sales' ? '#2563eb' : undefined }}></span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div
                            className="bg-white rounded-[2.5rem] p-12 border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center group relative overflow-hidden"
                            onMouseEnter={() => setHoveredUseCase('hr')}
                            onMouseLeave={() => setHoveredUseCase(null)}
                            style={{
                                backgroundImage: hoveredUseCase === 'hr'
                                    ? `linear-gradient(to bottom right, #9333ea0d, transparent)`
                                    : undefined
                            }}
                        >
                            <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
                                style={{ boxShadow: `0 10px 15px -3px #9333ea4d` }}>
                                {React.createElement(LuBriefcase as any, { className: "w-8 h-8" })}
                            </div>
                            <h3 className="text-3xl font-bold text-slate-900 mb-8 transition-colors duration-300"
                                style={{ color: hoveredUseCase === 'hr' ? '#9333ea' : undefined }}>HR & Operations</h3>
                            <ul className="space-y-4 text-left inline-block relative z-10">
                                {[
                                    "Resume parsing + smart scoring",
                                    "AI-generated interview invites & sync",
                                    "Automated onboarding documentation",
                                    "Intelligent report generation from data"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center text-slate-600 font-medium">
                                        <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mr-3 flex-shrink-0"
                                            style={{ backgroundColor: hoveredUseCase === 'hr' ? '#9333ea' : undefined }}></span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Implementation Path Section */}
            {/* Implementation Path Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-24">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>Process</span>
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">The Implementation Path</h2>
                    </div>

                    <div className="max-w-5xl mx-auto space-y-12">
                        {[
                            {
                                icon: LuSearch,
                                step: "1. Workflow Audit",
                                desc: "We map your current processes to identify automation gaps and AI opportunities."
                            },
                            {
                                icon: LuPenTool,
                                step: "2. System Design",
                                desc: "Creating a blueprint of tools, triggers, data flows, and specific AI agent roles."
                            },
                            {
                                icon: LuCode,
                                step: "3. Build & Integrate",
                                desc: "Developing workflows using n8n or Zapier, integrated with your existing CRM and APIs."
                            },
                            {
                                icon: LuSparkles,
                                step: "4. AI Intelligence",
                                desc: "Embedding OpenAI or Claude for classification, summarization, and decision-making."
                            },
                            {
                                icon: LuActivity,
                                step: "5. Monitor & Optimize",
                                desc: "Continuous testing and metric tracking to ensure peak performance and reliability."
                            }
                        ].map((item, index) => (
                            <div key={index} className="grid md:grid-cols-12 gap-4 items-start md:items-center">
                                <div className="md:col-span-5 flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        {React.createElement(item.icon as any, { className: "w-6 h-6 text-slate-900" })}
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900">{item.step}</h3>
                                </div>
                                <div className="md:col-span-7 pl-10 md:pl-0">
                                    <p className="text-slate-500 font-medium leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats & CTA Section */}
            {/* Stats Section */}
            <section className="py-20 bg-white">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-8 text-center max-w-5xl mx-auto">
                        <div className="p-8">
                            <div className="text-5xl md:text-6xl font-bold text-primary mb-4 tracking-tight">100+</div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Hours Saved Monthly</div>
                        </div>
                        <div className="p-8 md:border-l md:border-r border-gray-100">
                            <div className="text-5xl md:text-6xl font-bold text-secondary mb-4 tracking-tight">10x</div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Consistency Improvement</div>
                        </div>
                        <div className="p-8">
                            <div className="text-5xl md:text-6xl font-bold text-primary mb-4 tracking-tight">0</div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Manual Ops Errors</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Ready to scale your efficiency?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Let's build your intelligent workflow ecosystem today.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-bold shadow-lg gap-2"
                        >
                            Book an Audit
                            {React.createElement(LuArrowRight as any, { className: "w-5 h-5" })}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AIWorkflowAutomation;
